import { useSearchParams } from "react-router-dom";

export type SortColumn = "name" | "lastModified" | "size";
export type SortDirection = "asc" | "desc";
export type ExplorerView = "list" | "grid";

const DEFAULT_SORT: SortColumn = "name";
const DEFAULT_DIR: SortDirection = "asc";
const DEFAULT_VIEW: ExplorerView = "list";

export interface ExplorerUrlState {
  /** null means the explorer root (the folder list itself), not a specific folder. */
  folder: string | null;
  /** ADR 0010: a subfolder path within `folder`, "" at its root. Always "" when `folder` is null. */
  path: string;
  sort: SortColumn;
  dir: SortDirection;
  view: ExplorerView;
  /** Selecting a folder always resets `path` to its root. */
  setFolder: (folder: string | null) => void;
  /** Navigating within the same `folder` (breadcrumbs, opening a subfolder row). */
  setPath: (path: string) => void;
  /**
   * Sets `folder` and `path` together in one update: two separate
   * `setFolder`/`setPath` calls in the same event handler would each
   * build their next URLSearchParams from the same "previous" snapshot,
   * so the second call could win and silently drop the first (for
   * example a folder row's "Upload here" navigating across mounts).
   */
  navigate: (folder: string, path: string) => void;
  /** Clicking a column header: sorts by it ascending, or flips direction if it is already the active column. */
  toggleSort: (column: SortColumn) => void;
  setView: (view: ExplorerView) => void;
}

/**
 * The explorer's folder, path, sort and view state lives in the URL
 * (?folder=<key>&path=a/b&sort=name|lastModified|size&dir=asc|desc&view=list|grid),
 * not just component state, so a refresh or a deep link lands on the same
 * view (task requirement), the same reasoning as PartnerFilesPage's
 * earlier ?tab= fix. `path` is ADR 0010 (partner subfolders).
 */
export function useExplorerUrlState(): ExplorerUrlState {
  const [searchParams, setSearchParams] = useSearchParams();

  const folder = searchParams.get("folder");
  const path = folder ? (searchParams.get("path") ?? "") : "";
  const sortParam = searchParams.get("sort");
  const sort: SortColumn =
    sortParam === "lastModified" || sortParam === "size" ? sortParam : DEFAULT_SORT;
  const dir: SortDirection = searchParams.get("dir") === "desc" ? "desc" : DEFAULT_DIR;
  const view: ExplorerView = searchParams.get("view") === "grid" ? "grid" : DEFAULT_VIEW;

  function setView(nextView: ExplorerView) {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (nextView === DEFAULT_VIEW) {
          next.delete("view");
        } else {
          next.set("view", nextView);
        }
        return next;
      },
      { replace: true },
    );
  }

  function setFolder(nextFolder: string | null) {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (nextFolder) {
          next.set("folder", nextFolder);
        } else {
          next.delete("folder");
        }
        next.delete("path");
        return next;
      },
      { replace: true },
    );
  }

  function setPath(nextPath: string) {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (nextPath) {
          next.set("path", nextPath);
        } else {
          next.delete("path");
        }
        return next;
      },
      { replace: true },
    );
  }

  function navigate(nextFolder: string, nextPath: string) {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.set("folder", nextFolder);
        if (nextPath) {
          next.set("path", nextPath);
        } else {
          next.delete("path");
        }
        return next;
      },
      { replace: true },
    );
  }

  function toggleSort(column: SortColumn) {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        const currentSort = next.get("sort") ?? DEFAULT_SORT;
        const currentDir = next.get("dir") ?? DEFAULT_DIR;
        if (currentSort === column) {
          next.set("dir", currentDir === "asc" ? "desc" : "asc");
        } else {
          next.set("sort", column);
          next.set("dir", "asc");
        }
        return next;
      },
      { replace: true },
    );
  }

  return { folder, path, sort, dir, view, setFolder, setPath, navigate, toggleSort, setView };
}
